<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { capFirstChar, calculateTokenWidthByTitle } from '../../../../helpers/utilities';
	import {
		onSettingsResponse,
		getApplianceTypes,
		getReturnedEnergyTypes,
		castUsageStringToRelayUsageParam,
	} from '../../../../helpers/device_settings_helpers';
	import { requestEditDevice } from 'src/components/Lists/list-helpers';
	//Design system components
	import { Button, Token, TokenV2, Text, toast } from '@Shelly/shelly-design-system';
	//Types
	import type { ApplianceType } from '../../../../helpers/device_settings_helpers';
	import type { Device } from '../../../../types/Device';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	//Svelte hooks
	import { getContext, onDestroy, onMount } from 'svelte';
	import { postReq } from 'src/services/http';
	import { devices } from 'src/stores/lists/devices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import SettingGuide from '../../SettingGuide.svelte';

	export let device: Device;

	const types: ApplianceType[] = getApplianceTypes()
		.filter((at) => at.value !== 0)
		.map((at) => {
			at.title = capFirstChar(at.title);
			return at;
		});
	// will cap first until 'text-capitalize' class can be applied to <Select> options from outside

	const sources: ApplianceType[] = getReturnedEnergyTypes()
		.filter((at) => at.value !== 0)
		.map((at) => {
			at.title = capFirstChar(at.title);
			return at;
		});

	$: TokenComponent = $showNewLayout ? TokenV2 : Token;

	const save = async () => {
		if (!selected || device.relay_usage === selected.usage) {
			if (!$showNewLayout) {
				toast.open({
					type: 'info',
					title: $format('toast_select_different_appliance_type', {
						default: 'Please select a different appliance type to change it.',
					}),
				});
			}
			return;
		}

		waitForResponse = true;

		device.appliance_type = selected?.value;
		device.relay_usage = castUsageStringToRelayUsageParam(selected);
		const res = await requestEditDevice(device);

		if (res.isok) {
			savedUsage = device.relay_usage;
			if (device.relay_usage !== 'light') {
				const internalRes = await postReq('interface/device/set_cloud_options', {
					id: device.id,
					set: 'exclude_forgotten_lights',
					val: true,
				});
				if (internalRes.isok) {
					device.cloud_options.exclude_forgotten_lights = true;
					devices.update(device);
				}
			}
		}

		if (!initialSave) {
			onSettingsResponse(res, device, {
				success: selected?.title
					? $format('appliance_type_scc_toast', {
							values: { value: selected?.title },
							default: `Appliance type was changed to ${selected?.title}.`,
						})
					: $format('appliance_type_disabled_scc_toast', {
							default: 'Appliance type was disabled.',
						}),
			});
		} else {
			initialSave = false;
		}
		waitForResponse = false;
	};

	let selected: ApplianceType | undefined =
			types.find((type) => type.usage === device.relay_usage) ||
			sources.find((source) => source.usage === device.relay_usage),
		waitForResponse = false,
		initialSave = false,
		savedUsage = device.relay_usage,
		selectedSource =
			sources.find((at) => at.usage == device.relay_usage) ||
			sources.find((at) => at.usage == 'other');

	$: isSaveDisabled = waitForResponse || !selected || selected?.usage === savedUsage;

	const selectType = (type: ApplianceType) => {
		type.selected = !type.selected;
		selected = type;
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: save,
				loading: waitForResponse,
				disabled: isSaveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});

	onMount(() => {
		if (typeof selected?.usage === 'undefined') {
			selected = types.filter((at) => at.usage == 'other')[0];
			initialSave = true;
			save();
		}
		if (typeof selectedSource !== 'undefined') {
			selected = selectedSource;
		}
	});
</script>

<SettingGuide
	text={$format('settings_guide_energy_category', {
		default:
			'Tells Shelly what this device powers so energy dashboards can group usage — lighting, heating, cooking, and so on.',
	})}
/>

<Text styleType="body" class="mb-m">
	{$format('title_appliance_types', { default: 'Appliance types' })}
</Text>
<div class="d-flex flex-wrap gap-s mb-m">
	{#each types as type (type.usage)}
		<svelte:component
			this={TokenComponent}
			id={`token-appliance-${type.usage}`}
			size="m"
			title={type.title}
			class="text-nowrap"
			width={$showNewLayout ? undefined : calculateTokenWidthByTitle(type.title)}
			icon={`fas fa-${type.icon}`}
			selected={selected?.usage === type?.usage}
			on:click={() => {
				selectType(type);
			}}
		/>
	{/each}
</div>
<Text styleType="body" class="mb-m">
	{$format('title_consumption_source', { default: 'Source' })}
</Text>
<div class="d-flex flex-wrap gap-s mb-m">
	{#each sources as source (source.usage)}
		<svelte:component
			this={TokenComponent}
			id={`token-source-${source.usage}`}
			size="m"
			title={source.title}
			class="text-nowrap"
			width={$showNewLayout ? undefined : calculateTokenWidthByTitle(source.title)}
			icon={source.icon}
			selected={selected?.usage === source?.usage}
			on:click={() => {
				selectType(source);
			}}
		/>
	{/each}
</div>
{#if !$showNewLayout}
	<Button size="m" width="full" loading={waitForResponse} on:click={save}
		>{$format('button_save', { default: 'save' })}</Button
	>
{/if}
